import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Heading, Text, SimpleGrid, Flex, Icon, Image, Badge,
  Spinner, useToast,
} from '@chakra-ui/react';
import { FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import PageHero from '../Components/PageHero';
import { fetchRealisations, formatDate, localizeRow, normalizeStorageUrl } from '../lib/content';

const RealisationsSection = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [realisations, setRealisations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchRealisations().then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        console.error('REALISATIONS ERROR', error);
        toast({
          title: `${t('realisations.errorPrefix')}: ${error.message}`,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      }
      setRealisations(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [toast, t]);

  return (
    <Box pb={16}>
      <PageHero title={t('realisations.heroTitle')} subtitle={t('realisations.subtitle')} />
      <Box as="section" py={{ base: 14, md: 20 }} bg="surface.muted">
        <Container maxW="7xl">
          <Box maxW="700px" mx="auto" textAlign="center" mb={12}>
            <Heading as="h2" size="xl">
              {t('realisations.title')}
            </Heading>
            <Box w="56px" h="5px" bg="brand.500" borderRadius="full" mx="auto" mt={4} />
          </Box>

          {loading ? (
            <Flex justify="center" py={16}>
              <Spinner size="xl" color="brand.500" thickness="4px" />
            </Flex>
          ) : realisations.length === 0 ? (
            <Box
              bg="surface.base"
              borderRadius="2xl"
              boxShadow="sm"
              border="1px solid"
              borderColor="border.subtle"
              p={{ base: 12, md: 16 }}
              textAlign="center"
            >
              <Text color="text.soft" fontSize="lg">
                {t('realisations.comingSoon')}
              </Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              {realisations.map((realisation) => {
                const item = localizeRow(realisation, i18n.language);
                return (
                <Box
                  key={realisation.id}
                  bg="surface.base"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="border.subtle"
                  boxShadow="sm"
                  overflow="hidden"
                  transition="transform 0.25s ease, boxShadow 0.25s ease"
                  _hover={{ transform: 'translateY(-6px)', boxShadow: 'xl' }}
                >
                  {realisation.image ? (
                    <Image
                      src={normalizeStorageUrl(realisation.image)}
                      alt={item.title}
                      objectFit="cover"
                      w="100%"
                      h="200px"
                    />
                  ) : (
                    <Box h="200px" bgGradient="linear(to-br, brand.500, brand.700)" />
                  )}
                  <Box textAlign="center" p={8}>
                    <Heading as="h3" size="md" mb={3}>
                      {item.title}
                    </Heading>
                    {item.description && (
                      <Text color="text.muted" fontSize="sm" mb={6}>
                        {item.description}
                      </Text>
                    )}
                    <Flex justify="center" gap={3} flexWrap="wrap">
                      {realisation.location && (
                        <Badge colorScheme="brand" borderRadius="full" px={4} py={1.5} fontSize="sm">
                          <Icon as={FaMapMarkerAlt} mr={2} />
                          {realisation.location}
                        </Badge>
                      )}
                      {realisation.created_at && (
                        <Badge colorScheme="accent" color="brand.900" borderRadius="full" px={4} py={1.5} fontSize="sm">
                          <Icon as={FaCalendarAlt} mr={2} />
                          {formatDate(realisation.created_at, i18n.language)}
                        </Badge>
                      )}
                    </Flex>
                  </Box>
                </Box>
                );
              })}
            </SimpleGrid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default RealisationsSection;
